import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, KeyRound } from "lucide-react";
import { ApiError } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import {
  firstFlowError,
  kratosErrorCopy,
  nodeMessages,
  oidcProviders,
  submitOidcProvider,
} from "@/lib/kratos";
import { exchangeKratosSession } from "@/features/auth/kratos/exchange";
import { useKratosFlow } from "@/features/auth/kratos/useKratosFlow";
import {
  conditionalUiAvailable,
  flowSupportsPasskeyLogin,
  passkeyLogin,
  webauthnSupported,
} from "@/features/auth/kratos/webauthn";

/**
 * Kratos-mode sign-in: email + password form, passkey button, and
 * conditional-UI passkey autofill — all rendered by the SPA, zero
 * redirects. A completed Kratos ceremony is mirrored into the Tktic
 * app session via `POST /api/auth/session/exchange`.
 */
export function KratosLoginPanel({
  nextPath,
  flowId,
  autoExchange = false,
  selfServiceEnabled = true,
  socialLoginEnabled = false,
  googleEnabled = true,
  appleEnabled = true,
}: {
  nextPath: string;
  flowId?: string;
  autoExchange?: boolean;
  selfServiceEnabled?: boolean;
  socialLoginEnabled?: boolean;
  googleEnabled?: boolean;
  appleEnabled?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const [socialError, setSocialError] = useState<string | null>(null);
  const [socialBusy, setSocialBusy] = useState<string | null>(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const socialReturnTo = useMemo(
    () => socialAuthReturnTo("/login", nextPath),
    [nextPath],
  );

  const finishSignIn = async () => {
    setExchangeError(null);
    try {
      await exchangeKratosSession();
      await queryClient.invalidateQueries();
      await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      void navigate({ to: nextPath, replace: true });
    } catch (err) {
      setExchangeError(exchangeErrorCopy(err));
    }
  };

  const { flow, loading, submitting, error, submit } = useKratosFlow("login", {
    flowId,
    createParams: { return_to: socialReturnTo },
    onSuccess: finishSignIn,
  });
  // Latest submit in a ref so the one-shot conditional-UI ceremony
  // below doesn't restart every time the flow object changes identity.
  const submitRef = useRef(submit);
  submitRef.current = submit;

  const autoExchangeStarted = useRef(false);
  useEffect(() => {
    if (!autoExchange || autoExchangeStarted.current) return;
    autoExchangeStarted.current = true;
    void finishSignIn();
    // One-shot resume after a provider redirects back to /login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExchange]);

  // Conditional-UI autofill: arm one pending assertion per flow; when
  // the user picks a passkey from the email input's autofill UI, the
  // promise resolves and we auto-submit.
  const passkeyAvailable = !!flow && flowSupportsPasskeyLogin(flow);
  const flowForAutofill = passkeyAvailable ? flow : null;
  useEffect(() => {
    if (!flowForAutofill) return;
    const controller = new AbortController();
    let cancelled = false;
    void (async () => {
      if (!(await conditionalUiAvailable())) return;
      try {
        const assertion = await passkeyLogin(flowForAutofill, {
          conditional: true,
          signal: controller.signal,
        });
        if (!cancelled) {
          await submitRef.current({ method: "passkey", passkey_login: assertion });
        }
      } catch {
        // Aborted (unmount/re-arm) or dismissed — silent by design.
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // Re-arm only when the underlying flow (and its challenge) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowForAutofill?.id]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !flow) return;
    setExchangeError(null);
    await submit({ method: "password", identifier: email.trim(), password });
  };

  const handlePasskeyClick = async () => {
    if (!flow || passkeyBusy) return;
    setPasskeyBusy(true);
    setExchangeError(null);
    try {
      const assertion = await passkeyLogin(flow);
      await submit({ method: "passkey", passkey_login: assertion });
    } catch {
      // User dismissed the platform dialog — nothing to surface.
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleSocialClick = async (providerId: string) => {
    if (!flow || socialBusy) return;
    setSocialBusy(providerId);
    setSocialError(null);
    setExchangeError(null);
    try {
      const outcome = await submitOidcProvider("login", flow, providerId);
      if (outcome.kind === "success") {
        await finishSignIn();
      } else if (outcome.kind === "flow") {
        setSocialError(firstFlowError(outcome.flow) ?? "Social sign-in could not start.");
        setSocialBusy(null);
      }
    } catch (err) {
      setSocialError(kratosErrorCopy(err));
      setSocialBusy(null);
    }
  };

  const flowError = flow ? firstFlowError(flow) : null;
  const identifierMessages = flow ? nodeMessages(flow, "identifier") : [];
  const passwordMessages = flow ? nodeMessages(flow, "password") : [];
  const socialProviders = flow
    ? oidcProviders(flow).filter((provider) =>
        socialProviderAllowed(provider.id, {
          socialLoginEnabled,
          googleEnabled,
          appleEnabled,
        }) && !provider.disabled,
      )
    : [];

  if (loading) {
    return (
      <p data-testid="login-kratos-loading" className="font-mono text-xs text-ink-muted">
        Preparing sign-in...
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="login-kratos-panel">
      <form
        onSubmit={handlePasswordSubmit}
        data-testid="login-kratos-form"
        className="flex flex-col gap-4"
      >
        {socialProviders.length ? (
          <div
            data-testid="login-social-providers"
            className="grid gap-2 sm:grid-cols-2"
          >
            {socialProviders.map((provider) => (
              <Button
                key={provider.id}
                type="button"
                variant="outline"
                onClick={() => void handleSocialClick(provider.id)}
                disabled={!!socialBusy || submitting}
                data-testid={`login-social-${provider.id.toLowerCase()}`}
                className="w-full"
              >
                <SocialProviderMark providerId={provider.id} />
                {socialBusy === provider.id
                  ? "Opening..."
                  : `Continue with ${provider.label}`}
              </Button>
            ))}
          </div>
        ) : null}
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            // `webauthn` in autocomplete opts this input into the
            // passkey conditional-UI (autofill) flow armed above.
            autoComplete="username webauthn"
            autoFocus
            required
            data-testid="login-kratos-email"
            className="rounded border border-rule bg-canvas px-3 py-2.5 text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm"
          />
          {identifierMessages.map((message) => (
            <span key={message} className="font-mono text-[0.65rem] text-destructive">
              {message}
            </span>
          ))}
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Password
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            data-testid="login-kratos-password"
            className="rounded border border-rule bg-canvas px-3 py-2.5 text-[16px] text-ink focus:outline-none focus:ring-2 focus:ring-accent sm:text-sm"
          />
          {passwordMessages.map((message) => (
            <span key={message} className="font-mono text-[0.65rem] text-destructive">
              {message}
            </span>
          ))}
        </label>

        {flowError ? (
          <p data-testid="login-kratos-error" className="font-mono text-xs text-destructive">
            {flowError}
          </p>
        ) : null}
        {error ? (
          <p data-testid="login-kratos-client-error" className="font-mono text-xs text-destructive">
            {error}
          </p>
        ) : null}
        {socialError ? (
          <p data-testid="login-social-error" className="font-mono text-xs text-destructive">
            {socialError}
          </p>
        ) : null}
        {exchangeError ? (
          <div
            data-testid="login-kratos-exchange-error"
            className="flex flex-col gap-1 font-mono text-xs text-destructive"
          >
            <span>{exchangeError}</span>
            {exchangeError.includes("verify") ? (
              <Link
                to="/verify-email"
                data-testid="login-kratos-verify-link"
                className="self-start text-accent hover:underline"
              >
                Verify your email
              </Link>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
          {passkeyAvailable && webauthnSupported() ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => void handlePasskeyClick()}
              disabled={passkeyBusy || submitting}
              data-testid="login-kratos-passkey"
              className="w-full sm:w-auto"
            >
              <KeyRound aria-hidden="true" />
              {passkeyBusy ? "Waiting for passkey..." : "Sign in with a passkey"}
            </Button>
          ) : null}
          <Button
            type="submit"
            disabled={submitting}
            data-testid="login-kratos-submit"
            className="w-full sm:w-auto"
          >
            {submitting ? (
              "Signing in..."
            ) : (
              <>
                Sign in
                <ArrowRight aria-hidden="true" />
              </>
            )}
          </Button>
        </div>
      </form>

      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-rule pt-3 text-[0.65rem] font-mono text-ink-muted">
        <Link
          to="/recovery"
          data-testid="login-kratos-recovery"
          className="rounded px-1 py-0.5 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Forgot password?
        </Link>
        {selfServiceEnabled ? (
          <Link
            to="/register"
            data-testid="login-register"
            className="rounded px-1 py-0.5 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Create account
          </Link>
        ) : null}
      </footer>
    </div>
  );
}

function socialAuthReturnTo(pathname: "/login" | "/register", nextPath?: string): string {
  const url = new URL(pathname, window.location.origin);
  url.searchParams.set("social", "1");
  if (nextPath && nextPath !== "/hub") {
    url.searchParams.set("next", nextPath);
  }
  return url.toString();
}

function socialProviderAllowed(
  providerId: string,
  options: {
    socialLoginEnabled: boolean;
    googleEnabled: boolean;
    appleEnabled: boolean;
  },
): boolean {
  if (!options.socialLoginEnabled) return false;
  const id = providerId.trim().toLowerCase();
  if (id === "google") return options.googleEnabled;
  if (id === "apple") return options.appleEnabled;
  return false;
}

function SocialProviderMark({ providerId }: { providerId: string }) {
  const id = providerId.trim().toLowerCase();
  const label = id === "google" ? "G" : id === "apple" ? "ID" : "SSO";
  return (
    <span
      aria-hidden="true"
      className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded border border-rule bg-elevated px-1 font-display text-[0.62rem] font-semibold uppercase leading-none text-ink"
    >
      {label}
    </span>
  );
}

function exchangeErrorCopy(err: unknown): string {
  if (err instanceof ApiError) {
    const code =
      err.detail && typeof err.detail === "object" && "code" in err.detail
        ? String((err.detail as { code: unknown }).code)
        : "";
    if (code === "verified_email_required") {
      return "Please verify your email before signing in.";
    }
    if (code === "disabled_user") {
      return "This Tktic account is disabled. Contact your team admin for access.";
    }
    if (code === "social_login_disabled" || code === "social_provider_disabled") {
      return "That social sign-in option is currently disabled.";
    }
    if (code === "registration_disabled") {
      return "Account registration is currently closed. Contact your club admin for access.";
    }
    if (err.status === 429) {
      return "Too many sign-in attempts. Try again in a minute.";
    }
    return "Sign-in could not finish. Please try again.";
  }
  return "Network error. Try again.";
}
